import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import InitContext from "../pages/InitContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faChartLine,
  faEdit,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import SymptomsLineChart from "./SymptomsLineChart";
import {colors, Consts} from "../pages/consts";
import SymptomModules from "./SymptomModules";
import ReactDOM from "react-dom";
import {parseDate, sortData, toYearMonthDay} from "../pages/utils";

import SymptomImages from "./SymptomImages";
import Symptom from "./Symptom";
import SymptomTableHeader from "./SymptomTableHeader";

const SymptomsTable = ({
                         children,
                         selectedPatient,
                         hcpView,
                         printView = false,
                       }) => {
  const { t } = useTranslation();
  const [symptoms, setSymptoms] = useState([]);
  const [selectSymptomModule, setSelectSymptomModule] = useState(false);
  const [showSymptomsLineChart, setShowSymptomsLineChart] = useState(false);
  const [symptomsLineChart, setSymptomsLineChart] = useState([]);
  const [editDialogOpened, setEditDialogOpened] = useState(false);
  const [newSymptomOpened, setNewSymptomOpened] = useState(false);
  const [isNewDialogOpened, setIsNewDialogOpened] = useState(false);
  const [symptomToEdit, setSymptomToEdit] = useState(null);
  const context = useContext(InitContext);
  const [sortConfig, setSortConfig] = useState({key: "startDate", ascending: false});
  const [sortedSymptoms, setSortedSymptoms] = useState(symptoms ? [...symptoms] : []);

  const {
    loading,
    error,
    clearError,
    getDbSymptoms,
    deleteDbSymptom,
    saveDbSymptom,
    updateDbSymptom,
    saveDbSymptomPictures,
    deleteDbSymptomPicture,
  } = useMmcService();

  const patientId = !selectedPatient ? context?.patient?.id : selectedPatient?.id;

  useEffect(() => {
    clearError();

    if (patientId) {
      getDbSymptoms(context?.token, patientId).then((data) => {
        if (data) {
          setSymptoms(data);
        }
      });
    }
  }, [printView, patientId]);

  useEffect(() => {
    sortSymptomsForGui(symptoms);
  }, [symptoms, sortConfig]);

  const sortSymptomsForGui = (symptomsToSort) => {
    if (symptomsToSort.length > 0) {
      const sorted = sortSymptoms(sortConfig?.key, symptomsToSort, sortConfig?.ascending);
      setSortedSymptoms(sorted);
    } else {
      setSortedSymptoms([]);
    }
  }

  const sortSymptoms = (key, symptomsToSort, ascending) => {
    return sortData(key, symptomsToSort, ascending, item => item[key]);
  };

  const handleSortChange = (key) => {
    const ascending = sortConfig?.key === key ? !sortConfig?.ascending : true;
    setSortConfig({ key, ascending });
  };

  const handleDeleteSymptom = (symptomToDelete) => {
    const newSymptoms = symptoms?.filter(function (symptom) {
      return (
        (symptomToDelete?.id && symptom?.id !== symptomToDelete?.id) ||
        (symptom?.code !== symptomToDelete?.code &&
          symptom?.name !== symptomToDelete?.name &&
          symptom?.description !== symptomToDelete?.description)
      );
    });

    deleteDbSymptom(context?.token, symptomToDelete?.id).then(() =>
      setSymptoms(newSymptoms),
    );
  };

  const handleSaveNewSymptoms = (newSymptoms) => {
    if (newSymptoms !== null) {
      clearError();

      const patientId = !selectedPatient
        ? context?.patient?.id
        : selectedPatient?.id;

      saveDbSymptom(context?.token, newSymptoms, patientId).then(
        (transformedSymptoms) => {
          handleSymptomsFilesUpload(transformedSymptoms).then(() => {
            const newSymptoms = [...symptoms, ...transformedSymptoms];
            setSymptoms(newSymptoms);
            sortSymptomsForGui(newSymptoms);
          });
        },
      );
    }

    setSelectSymptomModule(false);
    setNewSymptomOpened(false);
    setEditDialogOpened(false);
  };

  const handleSymptomsFilesUpload = async (symptoms) => {
    for (const symptom of symptoms) {
      if (symptom?.files?.length > 0) {
        const formData = new FormData();

        symptom?.files?.forEach((file) => {
          formData?.append(Consts.FILES, file);
        });

        const response = await saveDbSymptomPictures(
          context?.token,
          formData,
          symptom?.id,
        );

        symptom.images = response?.map((img, index) => ({
          id: img?.image_id,
          file_name: symptom?.files[index]?.name, // Verwende den Dateinamen aus symptom?.files
        }));
      }
    }
  };

  const handleSymptomFilesUpload = async (symptomToAddFile, files) => {
    if (files?.length > 0) {
      const formData = new FormData();

      files?.forEach((file) => {
        formData?.append(Consts.FILES, file);
      });

      const response = await saveDbSymptomPictures(
        context?.token,
        formData,
        symptomToAddFile?.id,
      );

      symptomToAddFile.images = [
        ...(symptomToAddFile?.images || []),
        ...response?.map((img, index) => ({
          id: img?.image_id,
          file_name: files[index]?.name,
        })),
      ];

      setSymptomToEdit(symptomToAddFile);
    }
  };

  const handleShowSymptomsLineChart = (termToFilter) => {
    setSymptomsLineChart(termToFilter
      ? symptoms?.filter((item) => item?.term === termToFilter)
      : symptoms);

    setShowSymptomsLineChart(true);
  };

  const handleDeleteSymptomImage = async (symptomId, imageId) => {
    if (symptomId && imageId) {
      await deleteDbSymptomPicture(context?.token, imageId);

      setSymptoms((prevSymptoms) =>
        prevSymptoms?.map((symptom) => {
          if (symptom?.id === symptomId) {
            return {
              ...symptom,
              images: symptom?.images?.filter((img) => img?.id !== imageId),
            };
          }

          return symptom;
        }),
      );

      const newSymptom = {
        ...symptomToEdit,
        images: (symptomToEdit.images || []).filter(img => img.id === undefined ? true : img.id !== imageId)
      }

      setSymptomToEdit(newSymptom);
    }
  };

  const handleOpedEditDialog = (symptom) => {
    setIsNewDialogOpened(true);
    setSymptomToEdit(symptom);
  }

  const handleNewSymptom = (symptom) => {
    setIsNewDialogOpened(false);
    setNewSymptomOpened(true);
  }

  const handleExistingSymptom = (symptom) => {
    setIsNewDialogOpened(false);
    setEditDialogOpened(true);
  }

  const handleSaveEditedSymptom = (symptoms) => {
    setEditDialogOpened(false);

    if(symptoms !== null) {
      clearError();

      const patientId = !selectedPatient
        ? context?.patient?.id
        : selectedPatient?.id;

      updateDbSymptom(context?.token, symptoms[0], patientId).then(
        (res) => {
          getDbSymptoms(context?.token, patientId).then((data) => {
            if (data) {
              setSymptoms(data);
            }
          });
        }
      );
    }
  }

  let message =
    error && error?.includes(Consts.GET)
      ? t("error_by_retrieving_symptoms")
      : error && error?.includes(Consts.DELETE)
        ? t("error_by_deleting_a_symptom")
        : t("error_by_saving_a_symptom");

  const errorMessage = error && <ErrorMessage message={message} />;
  const spinner = !printView && loading && <Spinner />;

  return (
    <>
      {selectSymptomModule &&
        ReactDOM.createPortal(
          <SymptomModules
            onSaveNewSymptoms={handleSaveNewSymptoms}
            showDialog={selectSymptomModule}
            hcpView={hcpView}
            symptomsList={sortedSymptoms}
          />,
          document.getElementById(Consts.ROOT),
        )
      }
      {editDialogOpened &&
        <Symptom
          symptomParams={[symptomToEdit]}
          onUpdateSymptom={handleSaveEditedSymptom}
          onDeleteImage={handleDeleteSymptomImage}
          onAddImages={handleSymptomFilesUpload}
        />
      }
      {newSymptomOpened &&  //TODO: where is onAddImage?
        <Symptom
          symptomParams={[{term: symptomToEdit?.term, snomed: symptomToEdit?.snomed}]}
          category={symptomToEdit?.type}
          onSaveNewSymptoms={handleSaveNewSymptoms}
        />
      }
      {isNewDialogOpened &&
        <div className="feedback-modal">
          <div className="symptom-modal-content">
            <header className="symptom-modal-header">
              <FontAwesomeIcon icon={faXmark} onClick={() => setIsNewDialogOpened(false)} />
            </header>
            <div>{t("new_or_existing_symptom")}</div>
            <div className="w3-row">
              <button
                className="w3-btn w3-round-xlarge w3-col s3 w3-theme-d5 w3-margin-left w3-margin-top w3-padding-small"
                onClick={handleNewSymptom}>
                {t("new_symptom")}
              </button>
              <button
                className="w3-btn w3-round-xlarge w3-col s5 w3-theme-d5 w3-margin-left w3-margin-top w3-padding-small break-text"
                onClick={handleExistingSymptom}>
                {t("edit_symptom")}
              </button>
              <button
                className="w3-btn w3-round-xlarge w3-col s2 w3-theme-d5 w3-margin-left w3-margin-top w3-padding-small"
                onClick={() => setIsNewDialogOpened(false)}>
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      }
      {!editDialogOpened && showSymptomsLineChart &&
        ReactDOM.createPortal(
          <SymptomsLineChart
            symptoms={symptomsLineChart}
            onCloseDialog={() => setShowSymptomsLineChart(false)}
            showDialog={showSymptomsLineChart}
            patientId={patientId}
          />,
          document.getElementById(Consts.ROOT),
        )
      }
      {!editDialogOpened && !printView && !newSymptomOpened && (
        <SymptomTableHeader children={children} sortedSymptoms={sortedSymptoms}
                            onHandleShowSymptomsLineChart={handleShowSymptomsLineChart}
                            sortConfig={sortConfig} onHandleSortChange={handleSortChange}
                            selectSymptomModule={selectSymptomModule} printView={printView}
                            onSetSelectSymptomModule={setSelectSymptomModule} />
      )}
      {errorMessage}
      {spinner}
      {!editDialogOpened && !newSymptomOpened &&
        <div className={printView ? "w3-container w3-left-align" : "w3-container form-content-scroll"}>
          {sortedSymptoms?.length > 0 ? (
            <div className="w3-container">
              {sortedSymptoms?.map((item, i) => {
                return (
                  <div
                    className="w3-column w3-round-large w3-margin w3-padding-small w3-border w3-border-blue"
                    key={`symptom${i}`}
                  >
                    <div className="w3-row">
                      <div className="w3-col s9 w3-mobile">
                        <label>
                          {toYearMonthDay(item?.startDate)}: {t(item?.type)} { item?.endDate && item?.deactivated === 1
                          ? `(end date: ${toYearMonthDay(item?.endDate)})`
                          : ""}
                        </label>
                      </div>
                      <div className="w3-col s3 w3-mobile">
                        {!printView && (
                          <div className="w3-row w3-right">
                            { ( context?.role === Consts.HCP_PRESCRIBER || context?.role === Consts.PATIENT ) && <button
                              className="w3-btn w3-round-xlarge w3-theme-d5"
                              onClick={() => handleOpedEditDialog(item) }
                              disabled={editDialogOpened}
                            >
                              <FontAwesomeIcon icon={ faEdit }/>
                            </button>}
                            <button
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-margin-left"
                              onClick={() =>
                                handleShowSymptomsLineChart(item?.term)
                              }
                            >
                              <FontAwesomeIcon icon={faChartLine} />
                            </button>
                            { ( context?.role === Consts.HCP_PRESCRIBER || context?.role === Consts.PATIENT ) && <button
                              onClick={() => handleDeleteSymptom(item)}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-margin-left"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w3-row w3-padding-small">
                      <label className="bold-label">{t(item?.term)}</label>
                    </div>
                    <div className="w3-row w3-padding-small">
                      <div className="w3-col s2 w3-mobile">
                        <label>{t("severity")}: </label>
                      </div>
                      <div className="w3-col s10 w3-mobile">
                        <label
                          className="bold-label"
                          style={{ color: colors[item?.sufferingLevel] }}
                        >
                          {item?.sufferingLevel}
                        </label>
                      </div>
                    </div>
                    {item?.comment !== "" && (
                      <div className="w3-row w3-padding-small">
                        <div className="w3-col s2 w3-mobile">
                          <label>{t("comment")}:</label>
                        </div>
                        <div className="w3-col s10 w3-mobile">
                          {item?.comment}
                        </div>
                      </div>
                    )}
                    {(!printView || item?.images?.length > 0) && (
                      <SymptomImages
                        symptom={item}
                        onDeleteImage={handleDeleteSymptomImage}
                        onAddImages={handleSymptomFilesUpload}
                        printView={printView}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="w3-container w3-center">
              <label>{selectedPatient ? t("not_available") : t("you_have_not_yet_entered_any_symptoms")}</label>
            </div>
          )}
        </div>
      }
    </>
  );
};

export default SymptomsTable;
